import axios from "axios";
import http1 from "Utils/api";
import http2 from "Utils/api2";
import store from "redux/store";
import { handleAlert } from "redux/festures/alertSlice";
import { setServerDown, isServerDownError } from "Utils/serverStatus";

// Shared function to handle api errors
const handleApiError = (error) => {
  let message = "Something went wrong!";

  // Detect backend being down/unreachable and trigger the global maintenance screen
  if (isServerDownError(error)) {
    setServerDown(true);
  }

  if (error.response) {
    const data = error.response.data;
    message = data?.message || data?.error || error.response.statusText || message;
    
    // Check for session expiration / unauthorized access
    if (error.response.status === 401 || data?.message === "Session Expired") {
      localStorage.removeItem("admin_id");
      localStorage.removeItem("token");
      localStorage.removeItem("userType");
      if (window.location.pathname !== "/authentication/sign-in") {
        sessionStorage.setItem(
          "pending_alert",
          JSON.stringify({
            type: "error",
            msg: message || "Your account is disabled. Please contact the administrator.",
          })
        );
        window.location.href = "/authentication/sign-in";
      }
    }
  } else if (error.request) {
    message = "Network error. Please check your internet connection.";
  } else {
    message = error.message;
  }
  
  // Show global error toast using Redux
  if (!error.config?.suppressErrorToast) {
    store.dispatch(
      handleAlert({
        isOpen: true,
        type: "error",
        msg: message,
      })
    );
  }
  
  return Promise.reject(error);
};

// Response success interceptor to check for success: false inside 2xx responses
const handleApiSuccess = (response) => {
  // Any successful response means the server is reachable again
  setServerDown(false);
  if (response?.data && response.data.success === false) {
    const message = response.data.message || "Operation failed";
    store.dispatch(
      handleAlert({
        isOpen: true,
        type: "error",
        msg: message,
      })
    );
    // Check if session expired
    if (response.data.message === "Session Expired") {
      localStorage.removeItem("admin_id");
      localStorage.removeItem("token");
      localStorage.removeItem("userType");
      if (window.location.pathname !== "/authentication/sign-in") {
        window.location.href = "/authentication/sign-in";
      }
    }
  }
  return response;
};

// 1. Attach interceptors to global axios
axios.interceptors.response.use(handleApiSuccess, handleApiError);

// 2. Attach interceptors to http1 (api.js)
if (http1 && http1.interceptors) {
  http1.interceptors.response.use(handleApiSuccess, handleApiError);
}

// 3. Attach interceptors to http2 (api2.js)
if (http2 && http2.interceptors) {
  http2.interceptors.response.use(handleApiSuccess, handleApiError);
}

// 4. Monkeypatch native window.fetch
const originalFetch = window.fetch;
window.fetch = async function (...args) {
  try {
    const response = await originalFetch(...args);
    const clone = response.clone();
    
    if (!response.ok) {
      let message = "Request failed";
      try {
        const data = await clone.json();
        message = data?.message || data?.error || message;
        if (data?.message === "Session Expired") {
          localStorage.removeItem("admin_id");
          localStorage.removeItem("token");
          localStorage.removeItem("userType");
          if (window.location.pathname !== "/authentication/sign-in") {
            window.location.href = "/authentication/sign-in";
          }
        }
      } catch (e) {
        message = response.statusText || message;
      }
      
      store.dispatch(
        handleAlert({
          isOpen: true,
          type: "error",
          msg: message,
        })
      );
    } else {
      // Even if response status is 2xx, it might be a JSON payload with success: false
      try {
        const data = await clone.json();
        if (data && data.success === false) {
          store.dispatch(
            handleAlert({
              isOpen: true,
              type: "error",
              msg: data.message || "Operation failed",
            })
          );
          if (data.message === "Session Expired") {
            localStorage.removeItem("admin_id");
            localStorage.removeItem("token");
            localStorage.removeItem("userType");
            if (window.location.pathname !== "/authentication/sign-in") {
              window.location.href = "/authentication/sign-in";
            }
          }
        }
      } catch (e) {
        // Response is not JSON (e.g. PDF/text file), ignore
      }
    }
    return response;
  } catch (error) {
    store.dispatch(
      handleAlert({
        isOpen: true,
        type: "error",
        msg: error.message || "Network error. Please check your internet connection.",
      })
    );
    throw error;
  }
};
